import type { ErrorCode } from './types.ts';

/** WebSocket close codes the server uses (4000-4999 are for applications). */
export const CloseCode = {
  ENDED: 4000,
  SUPERSEDED: 4001,
  BAD_MESSAGE: 4400,
  UNAUTHORIZED: 4401,
  FORBIDDEN: 4403,
  NOT_FOUND: 4404,
  IDLE: 4408,
  RATE_LIMITED: 4429,
  INTERNAL: 4500,
} as const;
export type CloseCode = (typeof CloseCode)[keyof typeof CloseCode];

/** Codes the loader and SDK surface to host pages (a superset of the server's ErrorCode). */
export type WidgetErrorCode =
  | ErrorCode
  | 'config_not_found'
  | 'frame_blocked'
  | 'bot_disabled'
  | 'network'
  | 'mic_denied'
  | 'mic_unavailable'
  | 'camera_denied'
  | 'autoplay_blocked'
  | 'webgl_unavailable'
  | 'upload_rejected'
  | 'tool_failed'
  | 'tool_timeout'
  | 'protocol_mismatch';

/** One-line explanations, shown in the console with a link to the docs. */
export const ERROR_HELP: Partial<Record<WidgetErrorCode, string>> = {
  origin_not_allowed:
    "This site isn't in the bot's allowed origins. Add it in the admin panel: Bot > Security > Allowed origins.",
  config_not_found: 'No published bot with this id. Check data-bot, and publish the bot in the admin panel.',
  frame_blocked:
    "The chat frame didn't load. A Content-Security-Policy probably blocks it: add the chat host to frame-src.",
  identity_required: 'This bot requires identity verification: pass user.id and user.hash to WirefaceChat.boot().',
  identity_invalid: "user.hash doesn't match user.id. Compute HMAC-SHA256(identity secret, user.id) on your server.",
  mic_denied: 'Microphone permission was denied.',
  camera_denied: 'Camera permission was denied.',
  autoplay_blocked: 'The browser blocked sound until the visitor interacts with the chat.',
  webgl_unavailable: 'WebGL2 is not available, so a simpler face is shown.',
  rate_limited: 'Too many requests; slow down and try again shortly.',
  spend_cap_reached: "The bot reached today's usage cap set in the admin panel.",
};

export const docsUrl = (host: string, code: string) => `${host.replace(/\/$/, '')}/docs/reference/errors#${code}`;
